import {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {reduxForm} from 'redux-form';
import Header, {headerItemsLabels} from '../../../../Common/Header/header';
import {Prompt} from 'react-router-dom';
import Overlay from 'infrastructure/js/components/Overlay/overlay';
import useLabels from 'infrastructure/js/hooks/useLabels';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import Validation from 'infrastructure/js/components/controls/controlsValidations';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import RadioButton from 'infrastructure/js/components/controls/RadioButton/radioButton';
import {enumTypes, getEnumValue} from '../../../../../utils/enumHelper';
import QplTestsView from '../../QplTestsView/qplTestsView';
import {getQplOverallPassCriteriaOptions, FormModes} from '../../utils/qplHelper';

import './qplForm.scss';


function QplForm ({ history,
                    match,
                    handleSubmit,
                    pristine,
                    initialize,
                    initialData,
                    formData,
                    actions,
                    isViewOnly = false,
                  }) {



  const labels = useLabels('mat.forms.qplForm.');

  const [confirmedAbandon, setConfirmedAbandon] = useState(false);
  const nextLocationPath = useRef(null);

  const formMode = match.params.mode ?? FormModes.CREATE;

  useEffect(() => {
    let initialValues = {};
    if (formMode === FormModes.CREATE) {
      initialValues.overallPassCriteria = getEnumValue(enumTypes.OVERALL_PASS_CRITERIA)('ALL_PASS')
    }
    else if (initialData) {
      if (formMode === FormModes.EDIT) {
        initialValues.qplName = initialData.name;
        initialValues.qplVersion = initialData.version;
        initialValues.overallPassCriteria = initialData.overallPassCriteria;
        initialValues.customerSpecs = initialData.customerSpecs;
      } else if (formMode === FormModes.CLONE) {
        initialValues.overallPassCriteria = initialData.overallPassCriteria;
        initialValues.customerSpecs = initialData.customerSpecs;
      } else if (formMode === FormModes.UPDATE) {
        initialValues.qplName = initialData.name;
      }
    }

    initialize(initialValues);
  }, [initialData]);



  useEffect(() => {
    if (confirmedAbandon) {
      if (nextLocationPath.current) {
        history.push(nextLocationPath.current);
      }
      else if (nextLocationPath.current === '') {
        history.replace('/Administration/matSettings/qualityProductLists');
      }
      nextLocationPath.current = null;
    }
  }, [confirmedAbandon]);


  const getHeaderItems = () => {
    return {
      buttons: [
        {
          id: 'cancel',
          label: headerItemsLabels.CANCEL,
          className: 'no-icon',
          action: () => pristine ? history.replace('/Administration/matSettings/qualityProductLists') :
            actions.abandonDirtyForm(() => {
              setConfirmedAbandon(true);
              nextLocationPath.current = '';
            }),
        },
        {
          id: 'save',
          label: headerItemsLabels.SAVE,
          className: 'no-icon',
          action: handleSubmit(onSubmit),
          disabled: ( (formMode === FormModes.EDIT && pristine) ||
                      isViewOnly ||
                      formData?.get('loading'))
        },
      ],
    };
  };

  const onSubmit = (data) => {
    const submitData = {
      overallPassCriteria: data.overallPassCriteria,
      customerSpecs: data.customerSpecs,
    }

    if (formMode === FormModes.CREATE || formMode === FormModes.CLONE) {
      submitData.name = data.qplName;
      submitData.version = data.qplVersion;
    }
    if (formMode === FormModes.EDIT || formMode === FormModes.CLONE) {
      submitData.id = initialData?.id;
    }
    else if (formMode === FormModes.UPDATE) {
      submitData.id = initialData?.id;
      submitData.version = data.qplVersion;
    }

    actions.submit(submitData, formMode, onSubmitCallback);
  }

  const onSubmitCallback = (qpl) => {
    const initialValues = {
      qplName: qpl.name,
      qplVersion: qpl.version,
      overallPassCriteria: qpl.overallPassCriteria,
      customerSpecs: qpl.customerSpecs,
    }
    initialize(initialValues);

    nextLocationPath.current = formMode === FormModes.CREATE ? `${match.url}/${qpl?.id}/edit` : '';
    setConfirmedAbandon(true);
  }

  const getTitle = () => {
    if (isViewOnly) {
      return labels.get('title.view');
    }

    switch (formMode) {
      case FormModes.EDIT:
        return  labels.get('title.edit');
      case FormModes.CLONE:
        return  labels.get('title.clone');
      case FormModes.UPDATE:
        return  labels.get('title.updateVersion');
      default:
        return  labels.get('title.create');
    }
  }

  const getTitleDetails = () => {
    return (formMode !== FormModes.CREATE) ? ` - ${initialData?.name ?? ''} ${initialData?.version ?? ''}` : '';
  }

  const handleAbandon = (nextLocation) => {
    if (!confirmedAbandon) {
      actions.abandonDirtyForm(() => {
        setConfirmedAbandon(true);
        nextLocationPath.current = nextLocation.pathname;
      });
      return false;
    }
    return true;
  };

  const renderLoadingOverlay = () => {
    if (formData.get('loading')) {
      return <Overlay.Loading />;
    }
    return null;
  };

  return (
    <div className="pl-form qpl-form">
      <div className="title">
        <span>{getTitle()}</span>
        <span className="title-details">{getTitleDetails()}</span>
      </div>

      <div className='header-wrapper'>
        <Header className='form-header' {...getHeaderItems()} />
      </div>

      <div className='form-content'>
        <div className='qpl-params-section'>
          <InputSection label={labels.get('qplName') + '*'} htmlFor="qplName">
            <TextField id='qplName' name='qplName'
                       maxLength={100}
                       validate={Validation.required}
                       disabled={formMode === FormModes.EDIT || formMode === FormModes.UPDATE || isViewOnly}
            />
          </InputSection>
          <InputSection label={labels.get('qplVersion') + '*'} htmlFor="qplVersion">
            <TextField id='qplVersion' name='qplVersion'
                       maxLength={100}
                       validate={Validation.required}
                       disabled={formMode === FormModes.EDIT || isViewOnly}
            />
          </InputSection>
          {formMode !== FormModes.UPDATE &&
            <>
              <InputSection label={labels.get('overallPassCriteria')} htmlFor="overallPassCriteria">
                <RadioButton id="overallPassCriteria" name="overallPassCriteria" className="short-textfield"
                             labelsAndValues={getQplOverallPassCriteriaOptions()}
                             disabled={isViewOnly}
                />
              </InputSection>
              <InputSection label={labels.get('customerSpecs')} htmlFor="customerSpecs">
                <TextField id='customerSpecs' name='customerSpecs'
                           maxLength={50}
                           disabled={isViewOnly}
                />
              </InputSection>
            </>
          }
        </div>

        {formMode === FormModes.EDIT && !!initialData &&
          <QplTestsView history={history} match={match} qplId={initialData.id}
                        isViewOnly={isViewOnly}
          />
        }

      </div>

      <Prompt when={!pristine} message={handleAbandon}/>
      {renderLoadingOverlay()}
    </div>
  );
}


QplForm.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  initialize: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  actions: PropTypes.object.isRequired,
  formData: PropTypes.object.isRequired,
  isViewOnly: PropTypes.bool,
};

export default reduxForm({
  form: 'qplForm',
})(QplForm);





